// =============================================================================
// abnj-sso-subdomain.js - NJ Jamaat sign-in for njjamaat.org apps (the "subdomain kit")
// =============================================================================
//
// Copied as-is into every njjamaat.org app (the main website, access.njjamaat.org,
// and future apps at a subdomain or at njjamaat.org/<path>). How to add an app:
// README.md next to this file in the main website (website/src/sso/README.md).
//
// An app only needs two things:
//   1. verifySession()       "Who is signed in?" -> { exp, user } or null
//   2. signIn(returnUrl?)    if null: save this page, go to the main website's sign-in page
//
// Everything else happens on the main website's sign-in page
// (www.njjamaat.org/authenticate), the one page ITS sends people back to: it talks to
// ITS and the auth worker, sets the abnj_sso cookie for all of njjamaat.org, and then
// sends the user to this app's /callback (registered in backend/allowed-origins.js).
// The /callback page calls verifySession() and returns to the page saved by signIn().
//
// `user` is the member's profile from ITS: { itsId, name, gender, age, jamaat, jamaatInitials, jamiaat, extra }
//
// The app never sees the abnj_sso cookie (it's HttpOnly) and never holds a key. It
// asks the auth worker (auth.njjamaat.org), which only answers apps listed in
// backend/allowed-origins.js.
//
// -----------------------------------------------------------------------------
// USING THIS IN ANOTHER LANGUAGE OR FRAMEWORK
// -----------------------------------------------------------------------------
// Give this whole file to an AI assistant with the prompt below (fill in the <...>):
//
//   Convert the attached NJ Jamaat "subdomain kit" (abnj-sso-subdomain.js) to
//   <language / framework> for my app at <https://myapp.njjamaat.org>.
//   Keep the same behaviour, defaults and method names (verifySession, signIn):
//   - verifySession: POST {authUrl}/verify-session with the body "{}", sending the
//     abnj_sso cookie. 401 means "not signed in" (return null/None/nil). Any other
//     non-2xx is an error. 2xx with success: true returns { exp, user }.
//   - signIn(returnUrl): save the FULL URL of the page to come back to (default: the
//     current page) in the redirect_after_login cookie (value URL-encoded, Path=/,
//     Domain=.njjamaat.org, SameSite=Lax, Secure, expires in 15 minutes), then send
//     the user to signInUrl.
//   - Never read, decode or verify the abnj_sso cookie yourself, and add no keys or
//     secrets: only the auth worker can read it.
//   - Running in a browser: send the cookie with credentials: 'include' (or the
//     equivalent). Running on a server: forward the abnj_sso cookie from the incoming
//     request in a Cookie header, and do signIn as a 302 redirect with a Set-Cookie
//     header.
//   Also give me the app's /callback page or route: check verifySession(); if signed
//   in, delete redirect_after_login and go to the URL it holds, but only if that URL
//   is on this app's origin (otherwise go to "/"); if not signed in, call signIn("/").
//   Don't add anything else.
//
// Then register the app in the main website's backend/allowed-origins.js:
//   'https://myapp.njjamaat.org': { redirect_url: 'https://myapp.njjamaat.org/callback' },
// and redeploy the auth worker.
// =============================================================================

export const ABNJ_SSO_SUBDOMAIN_DEFAULTS = Object.freeze({
  /** The auth worker. */
  authUrl: 'https://auth.njjamaat.org',
  /** The main website's sign-in page, where every app sends people to sign in. */
  signInUrl: 'https://www.njjamaat.org/authenticate',
  /** Domain for the redirect_after_login cookie, so the sign-in page and /callback can read it. */
  cookieDomain: '.njjamaat.org',
  /** How long the redirect_after_login cookie lives while the user signs in. */
  returnCookieMinutes: 15,
});

/**
 * Creates the sign-in client for this app. Call once and reuse.
 * @param {Partial<typeof ABNJ_SSO_SUBDOMAIN_DEFAULTS>} [options]  override any default
 */
export function createAbnjSsoSubdomain(options = {}) {
  const cfg = { ...ABNJ_SSO_SUBDOMAIN_DEFAULTS, ...options };

  return {
    /**
     * Is someone signed in, and who? Also renews the session (1 hour from now).
     * @returns {Promise<{ exp: number, user: object } | null>}  null = not signed in
     * @throws with the worker's message for anything other than "not signed in"
     */
    async verifySession() {
      const res = await fetch(`${cfg.authUrl}/verify-session`, {
        method: 'POST',
        credentials: 'include', // send the HttpOnly abnj_sso cookie
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (res.status === 401) return null;
      if (!res.ok) throw new Error(`Auth worker /verify-session failed (${res.status}): ${await res.text()}`);
      const data = await res.json();
      return data?.success ? { exp: data.exp, user: data.user } : null;
    },

    /**
     * Saves the page to come back to (the redirect_after_login cookie) and goes to the
     * sign-in page. After sign-in, this app's /callback sends the user back there.
     * @param {string} [returnUrl]  a path or full URL on this app. Default: the current page.
     */
    signIn(returnUrl = window.location.href) {
      const full = new URL(returnUrl, window.location.href).href; // the sign-in page needs the app's origin
      const expires = new Date(Date.now() + cfg.returnCookieMinutes * 60 * 1000).toUTCString();
      const host = window.location.hostname;
      const domain = host === 'njjamaat.org' || host.endsWith('.njjamaat.org') ? `;domain=${cfg.cookieDomain}` : '';
      const secure = window.location.protocol === 'https:' ? ';Secure' : '';
      document.cookie = `redirect_after_login=${encodeURIComponent(full)};expires=${expires};path=/${domain};SameSite=Lax${secure}`;
      window.location.assign(cfg.signInUrl);
    },
  };
}
